import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import {
  Button,
  Chip,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  makeStyles,
  Box,
  IconButton,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import { Refresh as RefreshIcon } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import MaterialTable from "material-table";
import { AdminContainer } from "../components/layouts";
import ConfirmationDialog from "../components/ConfirmationDialog";
import api from "../api";
import config from "../config";
import tableIcons from "../components/tableIcons";
import Time from "../lithic/Moment";
import useToggle from "../state/useToggle";
import { useToastError } from "../components/FormError";
import { toast } from "react-toastify";
import {
  apiCollectionToRowResponse,
  rowQueryToApiParams
} from "../modules/tableQuery";

const RESOURCES = [
  { value: "rae", label: "RAE (Reporting Account Enrollment)" },
  { value: "ime", label: "IME (Identity Management Enrollment)" },
  { value: "customer", label: "Customer" },
  { value: "pms_enrollment", label: "PMS Enrollment" },
  { value: "universal_exclusion", label: "Universal Exclusion" }
];
const ALL_ACTIONS = [
  { value: "unenroll", label: "Unenroll" },
  { value: "backfill_existing", label: "Backfill Existing" },
  { value: "add_past_reporting", label: "Add Past Reporting" },
  { value: "update_contact_info", label: "Update Contact Info" },
  { value: "enroll_credit_monitoring", label: "Enroll in Credit Monitoring" },
  {
    value: "unenroll_credit_monitoring",
    label: "Unenroll from Credit Monitoring"
  },
  { value: "enroll", label: "Enroll" },
  { value: "create", label: "Create" }
];

const ACTIONS_BY_RESOURCE = {
  rae: ["unenroll", "add_past_reporting"],
  ime: ["unenroll", "backfill_existing"],
  customer: [
    "update_contact_info",
    "enroll_credit_monitoring",
    "unenroll_credit_monitoring"
  ],
  pms_enrollment: ["enroll"],
  universal_exclusion: ["create"]
};

const ENROLLMENT_TYPES = [
  { value: "required", label: "Required" },
  { value: "opt_in", label: "Opt In" },
  { value: "opt_out", label: "Opt Out" }
];

const STATUS_COLORS = {
  pending: "default",
  processing: "primary",
  completed: "primary",
  failed: "secondary"
};

export default function BulkUploadsListPage() {
  const classes = useStyles();
  const tableRef = React.useRef();
  const uploadToggle = useToggle();
  const detailsToggle = useToggle();
  const toastError = useToastError();

  const [file, setFile] = useState(null);
  const [resource, setResource] = useState("rae");
  const [action, setAction] = useState("unenroll");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);

  // PMS Enrollment specific state
  const [partnerId, setPartnerId] = useState(null);
  const [partnerSearchInput, setPartnerSearchInput] = useState("");
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [enrollmentType, setEnrollmentType] = useState("required");
  const [migratingFromPriorProvider, setMigratingFromPriorProvider] =
    useState(false);
  const [debouncedPartnerSearch] = useDebouncedValue(partnerSearchInput);

  const isPmsEnrollment = resource === "pms_enrollment";

  const availableActions = useMemo(() => {
    const allowedValues = ACTIONS_BY_RESOURCE[resource] || [];
    return ALL_ACTIONS.filter((a) => allowedValues.includes(a.value));
  }, [resource]);

  useEffect(() => {
    if (uploadToggle.on) {
      setFile(null);
      setResource("rae");
      setAction("unenroll");
      setIsSubmitting(false);
      setPartnerId(null);
      setPartnerSearchInput("");
      setPartnerOptions([]);
      setEnrollmentType("required");
      setMigratingFromPriorProvider(false);
    }
  }, [uploadToggle.on]);

  useEffect(() => {
    const allowedValues = ACTIONS_BY_RESOURCE[resource] || [];
    if (!allowedValues.includes(action)) {
      setAction(allowedValues[0] || "");
    }
  }, [resource, action]);

  useEffect(() => {
    if (!isPmsEnrollment || debouncedPartnerSearch.length < 2) {
      return;
    }

    const fetchPartners = async () => {
      setPartnerLoading(true);
      try {
        const resp = await api.getPartners({
          search: debouncedPartnerSearch,
          perPage: 20
        });
        const partners = resp.data?.items || resp.data || [];
        setPartnerOptions(partners);
      } catch (error) {
        setPartnerOptions([]);
        toastError("Failed to search partners. Please try again.");
      } finally {
        setPartnerLoading(false);
      }
    };

    fetchPartners();
  }, [debouncedPartnerSearch, isPmsEnrollment]);

  const handleFileChange = (event) => {
    const [selectedFile] = event.target.files || [];

    if (!selectedFile || selectedFile.type !== "text/csv") {
      toastError(new Error("Please select a valid CSV file"));
      event.target.value = null;
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async () => {
    if (!file) {
      toastError(new Error("Please select a CSV file to upload"));
      return;
    }

    if (isPmsEnrollment && !partnerId) {
      toastError(new Error("Please select a partner for PMS enrollment"));
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("resource", resource);
      formData.append("action", action);

      if (isPmsEnrollment) {
        formData.append("partner_id", partnerId);

        if (enrollmentType === "opt_in") formData.append("opt_in", true);
        if (enrollmentType === "opt_out") formData.append("opt_out", true);
        if (migratingFromPriorProvider)
          formData.append("migrating_from_prior_provider", true);
      }

      await api.createBulkUpload(formData);

      toast.success(
        "Bulk upload job created successfully. Processing will begin shortly."
      );
      uploadToggle.turnOff();
      tableRef.current && tableRef.current.onQueryChange();
    } catch (error) {
      const serverMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.messages?.join("\n") ||
        error.message ||
        "An error occurred while creating the bulk upload";

      toastError(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDetails = async (rowData) => {
    if (rowData.resource === "pms_enrollment") {
      try {
        const resp = await api.getBulkUploadStatus(rowData.id);
        setSelectedUpload({ ...rowData, ...resp.data });
      } catch {
        setSelectedUpload(rowData);
      }
    } else {
      setSelectedUpload(rowData);
    }
    detailsToggle.turnOn();
  };

  const refreshTable = useCallback(() => {
    tableRef.current && tableRef.current.onQueryChange();
  }, [tableRef]);

  const columns = [
    {
      title: "ID",
      field: "id",
      filtering: false,
      width: 80
    },
    {
      title: "Status",
      field: "status",
      filtering: true,
      render: (rowData) => (
        <Chip
          label={rowData.status}
          color={STATUS_COLORS[rowData.status] || "default"}
          size="small"
        />
      )
    },
    {
      title: "Resource",
      field: "resource",
      filtering: true,
      render: (rowData) => rowData.resource?.toUpperCase()
    },
    {
      title: "Action",
      field: "action",
      filtering: true,
      render: (rowData) =>
        ALL_ACTIONS.find((a) => a.value === rowData.action)?.label ??
        rowData.action
    },
    {
      title: "Initiated By",
      field: "initiatedByName",
      filtering: false,
      render: (rowData) => rowData.initiatedByName || "Unknown"
    },
    {
      title: "Progress",
      field: "progress",
      filtering: false,
      render: (rowData) => (
        <Typography variant="body2">
          {rowData.processedCount || 0} / {rowData.totalCount || 0}
          {rowData.failedCount > 0 && (
            <span className={classes.failedCount}>
              {" "}
              ({rowData.failedCount} failed)
            </span>
          )}
        </Typography>
      )
    },
    {
      title: "Created",
      field: "created_at",
      filtering: false,
      defaultSort: "desc",
      render: (rowData) => (
        <Time formatExact="MMM DD, YYYY HH:mm">{rowData.createdAt}</Time>
      )
    },
    {
      title: "Completed",
      field: "completed_at",
      filtering: false,
      render: (rowData) =>
        rowData.completedAt ? (
          <Time formatExact="MMM DD, YYYY HH:mm">{rowData.completedAt}</Time>
        ) : (
          "-"
        )
    },
    {
      title: "Actions",
      field: "actions",
      filtering: false,
      sorting: false,
      render: (rowData) => (
        <Button
          size="small"
          color="primary"
          onClick={() => handleViewDetails(rowData)}
        >
          View Details
        </Button>
      )
    }
  ];

  return (
    <AdminContainer variant="list">
      <MaterialTable
        title={
          <Box display="flex" alignItems="center">
            <Typography variant="h6">Bulk Uploads</Typography>
            <Tooltip title="Refresh">
              <IconButton
                onClick={refreshTable}
                size="small"
                style={{ marginLeft: 8 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
        tableRef={tableRef}
        columns={columns}
        options={{
          emptyRowsWhenPaging: false,
          filtering: true,
          pageSize: 50,
          pageSizeOptions: [50, 100, 200],
          sorting: true,
          thirdSortClick: false,
          filterCellStyle: {
            transform: "scale(0.80)",
            transformOrigin: "left",
            maxWidth: 0
          }
        }}
        data={(q) =>
          api
            .listBulkUploads(rowQueryToApiParams(q))
            .then((resp) => apiCollectionToRowResponse(resp))
        }
        icons={tableIcons}
        actions={[
          {
            isFreeAction: true,
            icon: () => <>+</>,
            tooltip: "Create Bulk Upload",
            onClick: () => uploadToggle.turnOn()
          }
        ]}
      />

      {/* Create Bulk Upload Dialog */}
      <ConfirmationDialog
        open={uploadToggle.on}
        onCancel={uploadToggle.turnOff}
        onConfirm={handleSubmit}
        title="Create Bulk Upload"
        cancelProps={{ children: "Cancel" }}
        confirmProps={{
          children: isSubmitting ? "Creating..." : "Create",
          disabled: isSubmitting || !file || (isPmsEnrollment && !partnerId)
        }}
      >
        <div className={classes.modalContent}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Upload a CSV file with IDs to perform bulk operations.
          </Typography>

          <FormControl
            fullWidth
            variant="outlined"
            className={classes.formControl}
          >
            <InputLabel>Resource</InputLabel>
            <Select
              value={resource}
              onChange={(e) => setResource(e.target.value)}
              label="Resource"
            >
              {RESOURCES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            variant="outlined"
            className={classes.formControl}
          >
            <InputLabel>Action</InputLabel>
            <Select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              label="Action"
            >
              {availableActions.map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isPmsEnrollment && (
            <>
              <Autocomplete
                key={uploadToggle.on ? "open" : "closed"}
                className={classes.formControl}
                options={partnerOptions}
                getOptionLabel={(option) =>
                  `${option.name || option.companyName || "Unknown"} (ID: ${option.id})`
                }
                loading={partnerLoading}
                onInputChange={(_, value) => setPartnerSearchInput(value)}
                onChange={(_, value) => setPartnerId(value?.id || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Partner"
                    variant="outlined"
                    placeholder="Search by name or ID..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {partnerLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />

              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
              >
                <InputLabel>Enrollment Type</InputLabel>
                <Select
                  value={enrollmentType}
                  onChange={(e) => setEnrollmentType(e.target.value)}
                  label="Enrollment Type"
                >
                  {ENROLLMENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                className={classes.formControl}
                control={
                  <Checkbox
                    checked={migratingFromPriorProvider}
                    onChange={(e) =>
                      setMigratingFromPriorProvider(e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Migrating from prior provider"
              />
              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.migratingFromPriorProviderHelp}
              >
                Skip opt-in/opt-out emails and waiting period. Use when
                residents were already enrolled with a prior rent-reporting
                provider.
              </Typography>
            </>
          )}

          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.csvHelp}
          >
            <strong>CSV Format:</strong>{" "}
            {resource === "rae" && (
              <>
                The file should have a header row with <code>rae_id</code>{" "}
                column. Each subsequent row should contain one RAE ID.
                {action === "add_past_reporting" ? (
                  <span className={classes.csvHelpNote}>
                    <strong>Note:</strong> Past reporting will be enabled for
                    each RAE, generating up to 24 months of rental verifications
                    (or until lease start date). Billing is handled separately
                    by Ops.
                  </span>
                ) : null}
              </>
            )}
            {resource === "ime" && (
              <>
                The file should have a header row with <code>customer_id</code>{" "}
                column.
                {action === "backfill_existing" && (
                  <>
                    {" "}
                    An optional <code>status</code> column is supported.
                  </>
                )}{" "}
                Each subsequent row should contain one customer ID.
              </>
            )}
            {resource === "customer" && action === "update_contact_info" && (
              <>
                The file should have a header row with <code>customer_id</code>{" "}
                column, plus optional <code>phone</code> and <code>email</code>{" "}
                columns. At least one of phone or email must be provided per
                row.
              </>
            )}
            {resource === "customer" &&
              action === "enroll_credit_monitoring" && (
                <>
                  The file should have a header row with{" "}
                  <code>customer_id</code> column. Each row should contain one
                  customer ID. The system will find the customer&apos;s
                  submitted enrollment case and enroll them in Equifax credit
                  monitoring. The partner is inferred automatically from the
                  enrollment case. Customers must have a submitted enrollment
                  case.
                </>
              )}
            {resource === "customer" &&
              action === "unenroll_credit_monitoring" && (
                <>
                  The file should have a header row with{" "}
                  <code>customer_id</code> column. Each row should contain one
                  customer ID. The system will find the customer&apos;s active
                  credit monitoring and unenroll them from Equifax. Customers
                  without active credit monitoring will be skipped.
                </>
              )}
            {isPmsEnrollment && (
              <>
                The file should have a header row with <code>id</code> or{" "}
                <code>lease_id</code> column containing PMS lease external IDs
                or PMI lease IDs. Each subsequent row should contain one lease
                ID.
              </>
            )}
            {resource === "universal_exclusion" && (
              <>
                The file must include <code>First name</code>,{" "}
                <code>Last name</code>, and at least one of: <code>Email</code>,{" "}
                <code>Phone</code>, <code>SSN</code>. Each subsequent row
                creates one universal exclusion entry. SSN values must be 9
                digits with no dashes.
              </>
            )}
          </Typography>

          <div className={classes.fileUpload}>
            <input
              accept=".csv"
              className={classes.fileInput}
              id="bulk-upload-csv"
              type="file"
              onChange={(e) => {
                handleFileChange(e);
                e.target.value = "";
              }}
            />
            <label htmlFor="bulk-upload-csv">
              <Button variant="outlined" component="span">
                {file ? file.name : "Select CSV File"}
              </Button>
            </label>
          </div>
        </div>
      </ConfirmationDialog>

      {/* View Details Dialog */}
      <ConfirmationDialog
        open={detailsToggle.on}
        onCancel={detailsToggle.turnOff}
        onConfirm={detailsToggle.turnOff}
        title={`Bulk Upload #${selectedUpload?.id || ""}`}
        cancelProps={{ style: { display: "none" } }}
        confirmProps={{ children: "Close" }}
      >
        {selectedUpload && (
          <div className={classes.detailsContent}>
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Status:</Typography>
              <Chip
                label={selectedUpload.status}
                color={STATUS_COLORS[selectedUpload.status] || "default"}
                size="small"
              />
            </div>
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Resource:</Typography>
              <Typography>{selectedUpload.resource?.toUpperCase()}</Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Action:</Typography>
              <Typography>
                {ALL_ACTIONS.find((a) => a.value === selectedUpload.action)
                  ?.label ?? selectedUpload.action}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Initiated By:</Typography>
              <Typography>
                {selectedUpload.initiatedByName || "Unknown"}
                {selectedUpload.initiatedByEmail && (
                  <span className={classes.initiatedByEmail}>
                    {" "}
                    ({selectedUpload.initiatedByEmail})
                  </span>
                )}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Total Count:</Typography>
              <Typography>{selectedUpload.totalCount}</Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Processed:</Typography>
              <Typography>{selectedUpload.processedCount}</Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Successful:</Typography>
              <Typography className={classes.successCount}>
                {(selectedUpload.processedCount || 0) -
                  (selectedUpload.failedCount || 0)}
              </Typography>
            </div>
            {selectedUpload.resource === "pms_enrollment" &&
              selectedUpload.enrollmentsCreatedCount != null && (
                <div style={{ paddingLeft: 16 }}>
                  <div className={classes.detailRow}>
                    <Typography variant="body2" color="textSecondary">
                      Enrollments Created:
                    </Typography>
                    <Typography variant="body2">
                      {selectedUpload.enrollmentsCreatedCount}
                    </Typography>
                  </div>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ fontStyle: "italic" }}
                  >
                    Enrollments are created asynchronously.
                    <br />
                    This count may increase over time — refresh for the latest.
                  </Typography>
                </div>
              )}
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Failed:</Typography>
              <Typography className={classes.failedCount}>
                {selectedUpload.failedCount || 0}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography variant="subtitle2">Created:</Typography>
              <Typography>
                <Time formatExact="MMM DD, YYYY HH:mm:ss">
                  {selectedUpload.createdAt}
                </Time>
              </Typography>
            </div>
            {selectedUpload.completedAt && (
              <div className={classes.detailRow}>
                <Typography variant="subtitle2">Completed:</Typography>
                <Typography>
                  <Time formatExact="MMM DD, YYYY HH:mm:ss">
                    {selectedUpload.completedAt}
                  </Time>
                </Typography>
              </div>
            )}

            {selectedUpload.errorMessage && (
              <div className={classes.errorSection}>
                <Typography variant="subtitle2" color="error">
                  Error Message:
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  minRows={3}
                  value={selectedUpload.errorMessage}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    className: classes.errorText
                  }}
                />
              </div>
            )}

            {selectedUpload.errors && selectedUpload.errors.length > 0 && (
              <div className={classes.errorSection}>
                <Typography variant="subtitle2" color="error">
                  Failed Items ({selectedUpload.errors.length}):
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  minRows={5}
                  maxRows={10}
                  value={selectedUpload.errors
                    .map((err) => {
                      const idParts = [];
                      if (err.customer_id)
                        idParts.push(`Customer: ${err.customer_id}`);
                      if (err.enrollment_id)
                        idParts.push(`Enrollment: ${err.enrollment_id}`);
                      if (idParts.length === 0) idParts.push(`ID: ${err.id}`);
                      return `${idParts.join(", ")} - ${err.reason}`;
                    })
                    .join("\n")}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    className: classes.errorText
                  }}
                />
              </div>
            )}

            {(selectedUpload.status === "completed" ||
              selectedUpload.status === "failed") && (
              <Box mt={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    window.location.href = `${config.apiHost}/admin/v1/bulk_uploads/${selectedUpload.id}/results_csv`;
                  }}
                >
                  Download Results CSV
                </Button>
              </Box>
            )}
          </div>
        )}
      </ConfirmationDialog>
    </AdminContainer>
  );
}

const useStyles = makeStyles((theme) => ({
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 400,
    padding: theme.spacing(1)
  },
  formControl: {
    marginTop: theme.spacing(1)
  },
  csvHelp: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    "& code": {
      backgroundColor: theme.palette.grey[300],
      padding: "2px 4px",
      borderRadius: 2
    }
  },
  csvHelpNote: {
    display: "block",
    marginTop: theme.spacing(1.5)
  },
  migratingFromPriorProviderHelp: {
    display: "block",
    marginTop: -theme.spacing(1),
    marginLeft: theme.spacing(4),
    marginBottom: theme.spacing(1)
  },
  fileUpload: {
    marginTop: theme.spacing(1)
  },
  fileInput: {
    display: "none"
  },
  detailsContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    minWidth: 400,
    padding: theme.spacing(1)
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.grey[200]}`
  },
  errorSection: {
    marginTop: theme.spacing(2)
  },
  errorText: {
    fontFamily: "monospace",
    fontSize: 12,
    backgroundColor: theme.palette.grey[50]
  },
  failedCount: {
    color: theme.palette.error.main,
    fontWeight: 500
  },
  successCount: {
    color: theme.palette.success.main,
    fontWeight: 500
  },
  initiatedByEmail: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  }
}));
